import CtaButton from '@/components/CtaButton';
import CtaLink, { SecondaryCtaLink } from '@/components/CtaLink';
import CtaText from '@/components/CtaText';
import FillImage from '@/components/FillImage';
import LoadingSpinner from '@/components/Icons/LoadingSpinner';
import RightCheveron from '@/components/Icons/RightCheveron';
import PageLayout from '@/components/PageLayout';
import { NavBarLightTheme } from '@/components/PageLayout/PageNavBar';
import PillDetail from '@/components/PillDetail';
import PopupDrawer from '@/components/PopupDrawer';
import ShopifyProvider, {
  useShopifyProvider,
} from '@/components/Provider/ShopifyProvider';
import ScreenOverlay from '@/components/ScreenOverlay';
import TwelveColGrid from '@/components/TwelveColGrid';
import { User, Variant, VariantWithShopify, WaitList } from '@/lib/interfaces';
import {
  CLIENT_ARTIST_PROFILE_URL,
  CLIENT_ARTIST_WAITLIST_URL,
  CLIENT_LOGIN_URL,
  CLIENT_SIGNUP_URL,
} from '@/utils/constants';
import { dollarfy, genQs } from '@/utils/v2HelperMethods';
import { postArtworkWaitlist } from '@/utils/v2HttpRequests';
import Link from 'next/link';
import { ReactNode, useState } from 'react';
//
export default function ReleasePage({
  variants,
  user,
  waitlist,
  redirect,
  releaseDateCopy,
  loginRequired = true,
  showInventoryLeft,
  buyNow = false,
  description,
  presaleShopifyLink,
  headerCopy,
}: {
  variants: Variant[];
  user: User | null;
  waitlist: WaitList | null;
  redirect: string;
  releaseDateCopy: string;
  loginRequired?: boolean;
  presaleShopifyLink?: JSX.Element;
  headerCopy?: string;
  showInventoryLeft: boolean;
  buyNow?: boolean;
  description: string | ReactNode;
}) {
  const [currentVariantIndex, setCurrentVariantIndex] = useState(0);

  const variant = variants[currentVariantIndex];

  const [submitting, setSubmitting] = useState(false);
  const [userWaitlist, setUserWaitlist] = useState(waitlist);

  const redirectParams = genQs({
    redirect,
  });

  const createWaitlist = async () => {
    setSubmitting(true);
    const { data } = await postArtworkWaitlist({
      prints: true,
      artist: variant.artist.id,
      author: user?.id as number,
      collectibles: false,
      stickers: false,
      sculptures: false,
      sculpture_budget: 0,
      painting_budget: 0,
      paintings: false,
      nft: false,
      apparel: false,
    });
    setUserWaitlist(data);
    setSubmitting(false);
  };

  const registerButtons = (() => {
    if (!user)
      return (
        <CtaLink
          href={CLIENT_LOGIN_URL + redirectParams}
          margin="mx-auto lgt:mx-0"
        >
          <CtaText>JOIN WAITLIST</CtaText>
        </CtaLink>
      );
    if (user && !userWaitlist)
      return (
        <CtaButton
          disabled={submitting}
          onClick={createWaitlist}
          margin="mx-auto lgt:mx-0"
        >
          {submitting ? (
            <>
              <CtaText>PROCESSING</CtaText>
              <LoadingSpinner />
            </>
          ) : (
            <CtaText>REGISTER</CtaText>
          )}
        </CtaButton>
      );
    if (user && userWaitlist)
      return (
        <>
          {/* <CtaButton margin="mx-auto lgt:mx-0" disabled>
            <CtaText>REGISTERED</CtaText>
          </CtaButton> */}
          <SecondaryCtaLink
            href={CLIENT_ARTIST_WAITLIST_URL(variant.artist.url_handle)}
            margin="mx-auto lgt:mx-0 mt-0.8"
          >
            <CtaText>VIEW YOUR WAITLIST</CtaText>
          </SecondaryCtaLink>
        </>
      );
  })();

  const ctaButtons = (() => {
    if (presaleShopifyLink) return presaleShopifyLink;
    else if (buyNow) {
      return (
        <BuyNowButton
          showInventoryLeft={showInventoryLeft}
          variant={variants[currentVariantIndex] as VariantWithShopify}
        />
      );
    } else {
      return registerButtons;
    }
  })();

  return (
    <ShopifyProvider>
      <PageLayout>
        <NavBarLightTheme sticky={false} />

        <TwelveColGrid my="my-24">
          {headerCopy && (
            <div className="col-span-12 mb-5.6 copy-l text-center">
              {headerCopy}
            </div>
          )}
          <VariantImagesWithThumbNails key={variant.id} variant={variant} />
          <div className="col-span-12 lgt:col-start-8 lgt:col-span-5 mt-2.4 lgt:mt-0 lgt:flex lgt:flex-col lgt:justify-between">
            <div className="flex flex-wrap justify-center lgt:justify-start">
              {variants.length > 1 &&
                variants.map((v, i) => (
                  <PillDetail
                    key={v.id}
                    copy={v.name}
                    onClick={() => setCurrentVariantIndex(i)}
                    addClassStyles={
                      'transition-all ease-in cursor-pointer flex-grow-0 w-max'
                    }
                    border={
                      i === currentVariantIndex
                        ? 'border border-black'
                        : 'border border-black border-opacity-10'
                    }
                  />
                ))}
            </div>
            <div>
              <div className="text-center text-body lgt:text-left lgt:mb-0.8">
                <Link
                  href={CLIENT_ARTIST_PROFILE_URL(variant.artist.url_handle)}
                >
                  <span className="font-medium">{variant.artist.name}</span>
                </Link>
              </div>
              <h3 className="text-center font-medium copy-l lgt:text-left mb-1.6">
                {variants[currentVariantIndex].name}
              </h3>
            </div>
            <div className="flex lgt:justify-start justify-center mb-3.2 max-w-xl lgt:max-w-none mx-auto lgt:mx-0">
              <div className="flex flex-col items-center w-1/3 mr-0.4 lgt:w-auto lgt:mr-3.2 lgt:items-start">
                <span className="text-black text-opacity-50 mb-0.8 text-s">
                  EDITION SIZE
                </span>
                <span className="text-xs font-medium">
                  {variant.editionNumber}
                </span>
              </div>
              <div className="flex flex-col items-center w-1/3 ml-0.4 lgt:w-auto lgt:mr-3.2 lgt:items-start">
                <span className="text-black text-opacity-50 mb-0.8 text-s">
                  RELEASE PRICE
                </span>
                <span className="text-xs font-medium">
                  {variant?.releasePrice > 0
                    ? dollarfy(variant.releasePrice)
                    : '-'}
                </span>
              </div>
              <div className="flex flex-col items-center w-1/3 ml-0.4 lgt:w-auto lgt:mr-0 lgt:items-start">
                <span className="text-black text-opacity-50 mb-0.8 text-s">
                  RELEASE DATE
                </span>
                <span className="text-xs font-medium text-center">
                  {releaseDateCopy}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap justify-center lgt:mb-3.2 mb-2.4 lgt:justify-start h-6.4 items-start">
              {[variant.medium, variant.cmDimensions, variant.inDimensions]
                .filter((v) => v?.length)
                .map((v) => (
                  <PillDetail key={v} copy={v as string} />
                ))}
            </div>
            <div className="text-center lgt:text-left text-black text-opacity-50 text-body mb-2.4 lgt:mb-3.2 mx-auto lgt:mx-0 max-w-xl">
              {description}
            </div>

            {ctaButtons}
          </div>
        </TwelveColGrid>

        {loginRequired && !user && (
          <ScreenOverlay open={!user} cbFunc={() => null}>
            <PopupDrawer
              open={!user}
              padding="p-1.6 lgt:p-0"
              height="h-[35rem] lgt:h-auto"
              width="w-screen"
            >
              <TwelveColGrid
                addClassStyles="h-full"
                width="w-full max-w-screen-lgt"
                my="lgt:my-16"
              >
                <div className="flex flex-col items-center col-span-12 lgt:items-start lgt:col-span-5">
                  <h2 className="font-medium text-body lgt:copy-desktop-m mb-1.6">
                    {variant.artist.name} &quot;{variant.name}&quot; Print
                    Release
                  </h2>
                  <p className="text-s">Pre-register now for early access.</p>
                </div>
                <div className="flex flex-col items-center col-span-12 mt-auto lgt:mt-0 lgt:col-span-4 lgt:col-start-9">
                  <CtaLink
                    margin="mb-1.6"
                    href={CLIENT_SIGNUP_URL + redirectParams}
                  >
                    <CtaText>SIGN UP</CtaText>
                    <RightCheveron stroke="white" />
                  </CtaLink>
                  <SecondaryCtaLink
                    margin="mb-1.6"
                    href={CLIENT_LOGIN_URL + redirectParams}
                  >
                    <CtaText>LOGIN</CtaText>
                    <RightCheveron />
                  </SecondaryCtaLink>
                </div>
              </TwelveColGrid>
            </PopupDrawer>
          </ScreenOverlay>
        )}
      </PageLayout>
    </ShopifyProvider>
  );
}

function VariantImagesWithThumbNails({ variant }: { variant: Variant }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = variant.image;

  return (
    <div className="flex flex-col items-center col-span-12 lgt:col-span-6">
      <FillImage
        priority
        src={images[currentImageIndex].url}
        height={'min-h-[27.6rem] h-[90vw] max-h-46.8'}
        width={'min-w-[27.6rem] w-full max-w-46.8'}
      />
      {images.length > 1 && (
        <div className="flex flex-wrap justify-center">
          {images.map((v, i) => (
            <button
              key={v.id}
              onClick={() => setCurrentImageIndex(i)}
              className="mx-0.8 hover:scale-110 transition-transform ease-in mt-1.6"
            >
              <FillImage
                priority
                src={v.url}
                objectFit={'cover'}
                height={'h-[6.2rem]'}
                width={'w-[6.2rem]'}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BuyNowButton({
  variant,
  showInventoryLeft,
}: {
  variant: VariantWithShopify;
  showInventoryLeft: boolean;
}) {
  const { buyNowExpressCheckout } = useShopifyProvider();

  const expressCheckout = async () => {
    const variantId = variant.shopify?.variants[0].admin_graphql_api_id;
    buyNowExpressCheckout(variantId as string);
  };
  const availableForPurchase =
    !!variant.shopify && variant.shopify.variants[0].inventory_quantity > 0;
  return (
    <div className="flex flex-col items-center lgt:items-start">
      {showInventoryLeft && availableForPurchase && (
        <div className="flex items-center mb-1.6 justify-center lgt:justify-start">
          <div className="w-[.8rem] h-[.8rem] bg-green-100 rounded-full mr-0.8"></div>
          <span className="text-s text-black-300">ONLY 5 LEFT</span>
        </div>
      )}
      {variant?.shopify?.variants &&
      variant?.shopify?.variants[0].inventory_quantity > 0 ? (
        <>
          <CtaButton height="h-4.8" onClick={expressCheckout}>
            PURCHASE
          </CtaButton>
          <div className="mt-0.8 text-black w-full max-w-xl text-s text-center">
            {dollarfy(Number(variant?.shopify?.variants[0].price))}
          </div>
        </>
      ) : (
        <CtaButton height="h-4.8" disabled>
          SOLD OUT
        </CtaButton>
      )}
    </div>
  );
}
